import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Image,
  Button,
  Form,
  Modal,
  InputGroup,
} from "react-bootstrap";
import { IoIosSearch } from "react-icons/io";
import { useNavigate } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";
import "../../../../SignUp/onBoarding/job-title.css";
import "./job.css";

import job from "../../../../../assets/images/job.png";
import { showAlert } from "../../../../../utils/alertUtils";
import { useCookies } from "../../../../../hooks/useCookies";
import Sidebar from "../../../../userflow/components/sidebar/sidebar";
import { useProfile } from "../../../../userflow/components/context/ProfileContext";

import * as userActions from "../../../../../actions/user";
import * as masterConstantActions from "../../../../../actions/masterConstant";

export const JobTitleUpdate = () => {
  const isFetched = useRef(false);
  const { updateProfile } = useProfile();
  const navigate = useNavigate();
  const { getCookie } = useCookies();
  const approvalStatus = getCookie("approval");

  const [loading, setLoading] = useState(false);
  const [jobTypes, setJobTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [customJobs, setCustomJobs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newJobTitle, setNewJobTitle] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [hasAddedJob, setHasAddedJob] = useState(false);

  const jobListEndRef = useRef(null);
  const jobListContainerRef = useRef(null);

  // Filtered jobs (including custom jobs)
  const filteredJobs = [...jobTypes, ...customJobs].filter((job) =>
    job.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Fetch master job types
  const fetchJobTypes = async () => {
    try {
      const response = await masterConstantActions.getConstantJobs();
      if (response.status && Array.isArray(response.data)) {
        const uniqueJobs = [];
        const jobSet = new Set();

        response.data.forEach((job) => {
          const jobName = job.name.trim().toLowerCase();
          if (!jobSet.has(jobName)) {
            uniqueJobs.push(job);
            jobSet.add(jobName);
          }
        });

        setJobTypes(uniqueJobs);
      }
    } catch (error) {
      console.error("Error fetching job types:", error);
    }
  };

  // Fetch user job
  const fetchUserJob = async () => {
    try {
      const response = await userActions.getUserJobs();
      if (response && response.jobs?.length > 0) {
        setSelectedOption(response.jobs[0].name);
      }
    } catch (error) {
      console.error("Error fetching user job:", error);
    }
  };

  useEffect(() => {
    if (isFetched.current) return;
    isFetched.current = true;

    fetchJobTypes();
    fetchUserJob();
  }, []);

  useEffect(() => {
    if (hasAddedJob && jobListEndRef.current && jobListContainerRef.current) {
      const container = jobListContainerRef.current;
      const element = jobListEndRef.current;

      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const isOutOfView =
        elementRect.bottom > containerRect.bottom ||
        elementRect.top < containerRect.top;

      if (isOutOfView) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }

      setHasAddedJob(false);
    }
  }, [hasAddedJob]);

  // Handle adding a custom job
  const handleSaveCustomJob = () => {
    const jobTitle = newJobTitle.trim();
    if (!jobTitle) {
      showAlert("Error", "Job title cannot be empty.");
      return;
    }

    const isDuplicate = [...jobTypes, ...customJobs].some(
      (job) => job.name.toLowerCase() === jobTitle.toLowerCase()
    );

    if (isDuplicate) {
      showAlert("Error", "Job title already exists.");
      return;
    }

    const newJob = { id: `custom-${customJobs.length + 1}`, name: jobTitle };
    setCustomJobs((prev) => [...prev, newJob]);
    setShowModal(false);
    setNewJobTitle("");
    setHasAddedJob(true);
  };

  // Save selected job title
  const saveJobTitle = async () => {
    if (!selectedOption) {
      showAlert("Error", "Please select a job title.");
      return;
    }

    setLoading(true);
    try {
      await userActions.updateUserJobs({ name: selectedOption });
      await updateProfile("update");

      const message =
        approvalStatus === "PENDING"
          ? "Approval request sent!"
          : "Successfully updated";

      const redirectPath =
        approvalStatus === "PENDING" ? "/user/home" : "/updateprofile";

      showAlert("Success", message, () => navigate(redirectPath));
    } catch (error) {
      console.error("Error saving job title:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sidebar>
      <div className="job-container" style={{ padding: "2rem" }}>
        <Container className="job-main">
          <Container className="job-content">
            <Container className="job-details">
              <div className="your-job">
                <Container className="job-text">
                  <Image
                    className="about-yourself-icon"
                    src={job}
                    alt="Job Icon"
                  />
                  <p>What’s your job title?</p>
                </Container>

                <InputGroup className="job-search-bar">
                  <Form.Control
                    className="job-search"
                    type="text"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <InputGroup.Text className="search-icon">
                    <IoIosSearch />
                  </InputGroup.Text>
                </InputGroup>

                <Container className="allJob" ref={jobListContainerRef}>
                  <div className="job-columns n-job-columns">
                    {filteredJobs.map((job, index) => (
                      <div
                        key={job.id || index}
                        onClick={() => setSelectedOption(job.name)}
                        className="job-item"
                        style={{
                          backgroundColor:
                            selectedOption === job.name
                              ? "#F7ECFF"
                              : "transparent",
                        }}
                      >
                        {job.name}
                      </div>
                    ))}
                    <div ref={jobListEndRef} />
                  </div>
                </Container>

                <div className="not-seeing-job">
                  Not seeing your job?{" "}
                   <button
                      className="btn btn-underline"
                      onClick={() => setShowModal(true)}
                    >
                      Add here
                    </button>
                </div>
              </div>
            </Container>

            <div
              className="d-flex justify-content-center"
              style={{
                gap: "30px",
                position: "fixed",
                bottom: "30px",
                width: "100%",
              }}
            >
              <button
                className="global-cancel-button"
                onClick={() => navigate("/updateprofile")}
                disabled={loading}
              >
                Cancel
              </button>
              <Button
                type="submit"
                variant="primary"
                className="btn-yes"
                onClick={saveJobTitle}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </Button>
            </div>
          </Container>
        </Container>

        <Modal
          className="customer-modal-without-header-footer"
          show={showModal}
          centered
          onHide={() => setShowModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title className="job-model-title">
              Add your job title
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="job-modal-body">
            <Form.Control
              className="job-model-form"
              type="text"
              placeholder="Type here...!"
              value={newJobTitle}
              onChange={(e) => setNewJobTitle(e.target.value)}
            />
          </Modal.Body>
          <Modal.Footer className="job-model-footer">
            <button
              className="global-cancel-button"
              onClick={() => setShowModal(false)}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="global-save-button"
              onClick={handleSaveCustomJob}
              disabled={loading}
            >
              Save
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </Sidebar>
  );
};
